import React, {
  type AnchorHTMLAttributes,
  Children,
  cloneElement,
  type ForwardRefExoticComponent,
  type ReactElement,
} from "react";
import type { BaseProps } from "@purpur/common-types";
import { IconHome } from "@purpur/icon/home";
import c from "classnames/bind";

import styles from "./breadcrumbs.module.scss";
import { type MetaListItem, metaListItem, metaSchema } from "./meta";

const cx = c.bind(styles);

type WithMeta = {
  meta?: true;
  baseUrl: string;
};

type WithoutMeta = {
  meta: false;
  baseUrl?: never;
};

export type BreadcrumbsProps = Omit<BaseProps<"nav">, "aria-label" | "children"> & {
  /** Describes the breadcrumb navigation for screen readers, e.g. "Breadcrumbs" or "Breadcrumb navigation". */
  ["aria-label"]: string;
  children: ReactElement<BreadcrumbsItemProps> | Array<ReactElement<BreadcrumbsItemProps>>;
  /** Set to render the breadcrumbs with light font color. Ideally used when rendered on dark backgrounds. */
  negative?: boolean;
  /** Set to render the last breadcrumb item in bold font to indicate that this is the current page. Setting this to false will render the last item as a normal breadcrumb link. Defaults to true. */
  highlightLast?: boolean;
  /** Set to not render the home icon in front of the first breadcrumb item. */
  hideHomeIcon?: boolean;
  linkElement?: ForwardRefExoticComponent<AnchorHTMLAttributes<HTMLAnchorElement>> | "a";
  /** The last breadcrumb will be a link and not have aria-current="page" */
  excludeCurrentPage?: boolean;
} & (WithMeta | WithoutMeta);

export type BreadcrumbsItemProps = Omit<BaseProps<"li">, "onClick" | "children"> & {
  children: string;
  href?: string;
  onClick?: () => void;
  /** @ignore */
  current?: boolean;
  /** @ignore */
  first?: boolean;
  /** @ignore */
  last?: boolean;
  /** @ignore */
  linkElement?: BreadcrumbsProps["linkElement"];
  /** @ignore */
  meta?: boolean;
  /** @ignore */
  negative?: boolean;
  /** @ignore */
  excludeCurrentPage?: boolean;
};

const rootClassName = "purpur-breadcrumbs";
const itemClassName = "purpur-breadcrumb-item";

const Breadcrumbs = ({
  ["aria-label"]: ariaLabel,
  baseUrl,
  children,
  className,
  excludeCurrentPage = false,
  linkElement = "a",
  meta = true,
  negative = false,
  highlightLast = true,
  hideHomeIcon,
  ...props
}: BreadcrumbsProps) => {
  const classes = cx([
    rootClassName,
    `${rootClassName}--${negative ? "negative" : "default"}`,
    className,
  ]);
  const maxIndex = Children.count(children);

  const metaListItems: MetaListItem[] = [];

  const items = Children.map(children, (item, index) => {
    const position = index + 1;
    const last = maxIndex === position;
    const current = highlightLast && last;

    const { children, href } = item.props;

    metaListItems.push(metaListItem(children, position, href && `${baseUrl}${href}`));

    const child = cloneElement(item, {
      className: item.props.className,
      current,
      negative,
      first: !hideHomeIcon && position === 1,
      last,
      linkElement,
      excludeCurrentPage,
    });

    return child;
  });

  const schema = metaListItems.length === maxIndex ? metaSchema(metaListItems) : null;

  return (
    <nav aria-label={ariaLabel} className={classes} {...props}>
      <ol className={styles[`${rootClassName}__list`]}>{items}</ol>
      {meta && schema ? (
        <script
          type="application/ld+json"
          data-testid="breadcrumbs-meta"
          dangerouslySetInnerHTML={{ __html: schema }}
        />
      ) : null}
    </nav>
  );
};

const Item = ({
  href,
  ["data-testid"]: dataTestId,
  children,
  current = false,
  linkElement = "a",
  negative = false,
  onClick,
  first = false,
  last = false,
  excludeCurrentPage = false,
  ...props
}: BreadcrumbsItemProps) => {
  const LinkElement = linkElement;
  const classes = cx(
    [itemClassName],
    styles[`${itemClassName}--${negative ? "negative" : "default"}`],
    {
      [`${itemClassName}--current`]: !excludeCurrentPage && current,
    }
  );

  return (
    <li {...props} className={classes}>
      {!last || excludeCurrentPage ? (
        <LinkElement
          href={href}
          data-testid={dataTestId}
          onClick={onClick}
          className={cx(`${itemClassName}__element`, `${itemClassName}__link`)}
        >
          {first && (
            <span className={styles[`${itemClassName}__home`]} aria-hidden="true">
              <IconHome size="xs" />
            </span>
          )}
          {children}
        </LinkElement>
      ) : (
        <span
          aria-current="page"
          data-testid={dataTestId}
          className={cx(`${itemClassName}__element`)}
        >
          {children}
        </span>
      )}
    </li>
  );
};

Breadcrumbs.Item = Item;

export { Breadcrumbs, Item as BreadcrumbsItem };
